import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';

import {ApplicationKeysService} from './application-keys.service';
import {ApisService} from '../apis.service';
import {KeyItem} from '../key-item';

const FEEDBACK_TIMEOUT_VALUE = 2000;

@Component({
  selector: 'aqx-application-keys',
  templateUrl: './application-keys.component.html',
  styleUrls: ['./application-keys.component.scss']
})
export class ApplicationKeysComponent implements OnInit {
  @Input() environmentsAvailable: string[];
  @Input() exampleResource: string[];
  @Input() invocationUri: string;
  @Input() keys: KeyItem [];
  accessToken: string;
  accessTokenResponse: string;
  applicationKeysForm: FormGroup;
  gatewayBaseUriHasPort: boolean;
  grantTypesAvailable;
  oauthTokenUri: string;
  oauthTokenUriHasPort: boolean;
  keysAvailable: KeyItem [];

  generatingAccessToken;
  tokenApiCallKeyTypeChange;
  tokenApiCallGrantTypeChange;
  tokenApiResponseChange;
  tokenApiCallCopied;
  exampleApiCallCopied;
 
  constructor(private apisService: ApisService, private applicationKeysService: ApplicationKeysService) {
    this.grantTypesAvailable = this.applicationKeysService.getGrantTypesAvailable();
    this.oauthTokenUri = this.apisService.getOauthTokenUri();
    this.oauthTokenUriHasPort = this.apisService.getOauthTokenUriHasPort();
    this.gatewayBaseUriHasPort = this.apisService.getGatewayBaseUriHasPort();
  }

  ngOnInit() {
    // Sort keys so alphabetical resulting in PRODUCTION always appears before SANDBOX, in both the table and the select control.
    this.keysAvailable = this.applicationKeysService.getKeysAvailable(this.keys, this.environmentsAvailable);
    this.applicationKeysForm = this.createFormGroup();
    this.generateAccessToken();
  }

  private createFormGroup() {
    return new FormGroup({
      keyItemSelected: new FormControl(this.keysAvailable[0]),
      grantTypeSelected: new FormControl(this.grantTypesAvailable[0]),
      grantTypeUser: new FormControl(),
      grantTypePassword: new FormControl(),
      grantTypeRefreshToken: new FormControl()
    });
  }

  generateAccessToken(showFeedback?: boolean, isTokenApiCallKeyTypeChange?: boolean, isTokenApiCallGrantTypeChange?: boolean) {
    if (isTokenApiCallKeyTypeChange) {
      this.tokenApiCallKeyTypeChanged();
    }
    if (isTokenApiCallGrantTypeChange) {
      this.tokenApiCallGrantTypeChanged();
    }
    const grantTypeSelected = this.applicationKeysForm.controls.grantTypeSelected.value;
    const requiresUserAndPassword = grantTypeSelected.requiresUserAndPassword;
    const requiresRefreshToken = grantTypeSelected.requiresRefreshToken;
    let isGenerate = true;
    if ((requiresUserAndPassword &&
      (!this.applicationKeysForm.controls.grantTypeUser.value || !this.applicationKeysForm.controls.grantTypePassword.value)) ||
      (requiresRefreshToken && !this.applicationKeysForm.controls.grantTypeRefreshToken.value)) {
      grantTypeSelected.accessToken = undefined;
      grantTypeSelected.accessTokenResponse = undefined;
      isGenerate = false;
      this.tokenChanged();
    }
    if (isGenerate) {
      if (showFeedback) {
        this.generatingAccessToken = true;
      }
      const keyItemSelected = this.applicationKeysForm.controls.keyItemSelected.value;
      this.apisService.generateAccessToken(
        keyItemSelected.consumerKey,
        keyItemSelected.consumerSecret,
        grantTypeSelected.name,
        requiresUserAndPassword ? this.applicationKeysForm.controls.grantTypeUser.value : undefined,
        requiresUserAndPassword ? this.applicationKeysForm.controls.grantTypePassword.value : undefined,
        requiresRefreshToken ? this.applicationKeysForm.controls.grantTypeRefreshToken.value : undefined
      )
      .subscribe(accessTokenResponse => {

        grantTypeSelected.accessToken = accessTokenResponse.access_token;
        grantTypeSelected.accessTokenResponse = accessTokenResponse; // JSON.stringify(accessTokenResponse);
        grantTypeSelected.isError = false;

        this.tokenChanged();
      }, () => {
        grantTypeSelected.accessToken = undefined;
        grantTypeSelected.accessTokenResponse = undefined;
        grantTypeSelected.isError = true;
        this.tokenChanged();
      }, () => {
        this.generatingAccessToken = false;
      });
    }
  }

  getExampleApiCurl() {
    const accessToken = this.applicationKeysForm.controls.grantTypeSelected.value.accessToken || '$ACCESS_TOKEN';
    let curl = `curl ${this.invocationUri}${this.exampleResource}`;
    if (this.gatewayBaseUriHasPort) {
      curl += ' -k';
    }
    curl += ` -H 'Authorization: Bearer ${accessToken}'`;
    return curl;
  }

  private tokenApiCallKeyTypeChanged() {
    this.tokenApiCallKeyTypeChange = true;
    setTimeout(() => {
      this.tokenApiCallKeyTypeChange = false;
    }, FEEDBACK_TIMEOUT_VALUE);
  }

  tokenApiCallGrantTypeChanged() {
    this.tokenApiCallGrantTypeChange = true;
    setTimeout(() => {
      this.tokenApiCallGrantTypeChange = false;
    }, FEEDBACK_TIMEOUT_VALUE);
  }

  private tokenChanged() {
    this.tokenApiResponseChange = true;
    setTimeout(() => {
      this.tokenApiResponseChange = false;
    }, FEEDBACK_TIMEOUT_VALUE);
  }

  copyTokenApiCall() {
    this.copyText(this.applicationKeysService.getOauthTokenCurl(
      this.applicationKeysForm.controls.keyItemSelected.value,
      this.applicationKeysForm.controls.grantTypeSelected.value,
      this.applicationKeysForm.controls.grantTypeUser.value,
      this.applicationKeysForm.controls.grantTypePassword.value,
      this.applicationKeysForm.controls.grantTypeRefreshToken.value,
    ));
    this.tokenApiCallCopied = true;
    setTimeout(() => {
      this.tokenApiCallCopied = false;
    }, 1000);
  }

  copyExampleApiCall() {
    this.copyText(this.getExampleApiCurl());
    this.exampleApiCallCopied = true;
    setTimeout(() => {
      this.exampleApiCallCopied = false;
    }, 1000);
  }

  private copyText(val: string) {
    // taken from https://stackoverflow.com/questions/49102724/angular-5-copy-to-clipboard
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }
}
